import { CheckCircle, Warning, XCircle } from "@phosphor-icons/react";

function StatusPill({ coverage }) {
  if (coverage === "strong") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
        <CheckCircle size={14} weight="fill" /> Strong
      </span>
    );
  }
  if (coverage === "weak") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700">
        <Warning size={14} weight="fill" /> Needs work
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700">
      <XCircle size={14} weight="fill" /> Missing
    </span>
  );
}

/**
 * Customer-facing AEO section for the Business Growth Assessment.
 *
 * Deliberately framed as a website opportunity, not a technical AEO score.
 * AI Visibility answers "are you appearing?"; this block helps explain whether
 * the website is prepared to become part of the answer.
 */
export default function AnswerVisibilityBlock({ analysis }) {
  if (!analysis) return null;

  const readiness = analysis.answer_readiness;
  const findings = analysis.question_findings || [];
  const opportunities = analysis.opportunities || [];

  return (
    <section className="bento p-6 lg:p-8" data-testid="answer-visibility-block">
      <div className="label-tiny mb-2 text-[#2D5BFF]">Website Analysis</div>
      <h2 className="font-display text-2xl lg:text-3xl font-black tracking-tight text-zinc-900">
        Answer Visibility Opportunities
      </h2>
      <p className="text-sm text-zinc-600 mt-2 max-w-3xl leading-relaxed">
        Customers are increasingly asking Google and AI direct questions before choosing a business.
        We looked at whether your website clearly answers the questions most likely to influence that decision.
      </p>

      {analysis.analysis_status !== "complete" ? (
        <div className="mt-5 rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700">
          Website analysis could not be completed reliably during this run. RankTrust did not infer answer gaps from incomplete website data. This may reflect how the site responds to automated systems rather than a customer-facing outage, so this signal should be rechecked before making a recommendation.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
            <div className="rounded-lg border border-zinc-200 p-4">
              <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Strong Answers</div>
              <div className="font-display text-2xl font-black text-zinc-900 mt-1">{readiness?.strong_answers ?? 0}</div>
            </div>
            <div className="rounded-lg border border-zinc-200 p-4">
              <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Needs Work</div>
              <div className="font-display text-2xl font-black text-zinc-900 mt-1">{readiness?.weak_answers ?? 0}</div>
            </div>
            <div className="rounded-lg border border-zinc-200 p-4">
              <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Missing Answers</div>
              <div className="font-display text-2xl font-black text-zinc-900 mt-1">{readiness?.missing_answers ?? 0}</div>
            </div>
          </div>

          {findings.length > 0 && (
            <div className="mt-7">
              <div className="label-tiny mb-3">Questions customers may be asking</div>
              <div className="divide-y divide-zinc-200 border-y border-zinc-200">
                {findings.slice(0, 6).map((item, index) => (
                  <div key={`${item.question}-${index}`} className="py-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 md:items-start">
                    <div>
                      <div className="text-sm font-semibold text-zinc-900">{item.question}</div>
                      {item.opportunity && (
                        <div className="text-xs text-zinc-600 mt-1 leading-relaxed">{item.opportunity}</div>
                      )}
                    </div>
                    <StatusPill coverage={item.coverage} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {opportunities.length > 0 && (
            <div className="mt-7 rounded-xl bg-zinc-950 text-white p-5 lg:p-6">
              <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400">Highest-value opportunity</div>
              <div className="font-display text-lg font-bold mt-2 leading-snug">
                {opportunities[0].question || opportunities[0].finding}
              </div>
              {opportunities[0].question && (
                <div className="text-sm text-zinc-300 mt-2 leading-relaxed">{opportunities[0].finding}</div>
              )}
            </div>
          )}
        </>
      )}

      <p className="text-xs text-zinc-500 mt-5 leading-relaxed">
        Answer readiness is different from live AI visibility. It evaluates whether the website gives search and answer engines clear, useful information they can understand and cite.
      </p>
    </section>
  );
}
